import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/lib/motion";

const SECTIONS = [
  {
    title: "What we collect",
    body: "Only what you hand us yourself: your name, email, website and message when you use the contact form, the free SEO snapshot, the booking calendar, the newsletter signup, or the AI checklist email signup. No registries, no resale lists, nothing scraped.",
  },
  {
    title: "How we use it",
    body: "To answer your message, send what you asked for (a snapshot, a booking confirmation, the AI checklist, Todd's SEO tips), and follow up on your request. Your details live in our own database and are never sold or rented to anyone.",
  },
  {
    title: "Cookies & analytics",
    body: "We use Google Analytics to see which pages are useful — page views and rough location, nothing personal. You can block cookies in your browser and the site keeps working.",
  },
  {
    title: "Emails",
    body: "Every email we send comes from us — usually Todd — and replying reaches a human directly. No trackers hiding in the pixels, no purchased lists.",
  },
  {
    title: "Your choices",
    body: "Want off the list, or your details removed entirely? Reply to any email or write todd@zddesignagency.com and we'll take care of it promptly.",
  },
];

export default function PrivacyPage() {
  return (
    <main data-testid="privacy-page">
      <Seo
        title="Privacy Policy — ZD Design"
        description="The plain-English ZD Design privacy policy: what we collect, why we use it, and how to reach a human when you have a question."
        path="/privacy"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-16 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Link
              to="/"
              data-testid="privacy-back-link"
              className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back home
            </Link>
            <Eyebrow>Privacy</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              Privacy, in plain English<span className="text-flame">.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Short version: we only collect what you give us, we only use it
              to help you, and we never sell it. The details:
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <div className="space-y-4">
            {SECTIONS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.05}>
                <div
                  data-testid={`privacy-section-${i + 1}`}
                  className="glass rounded-2xl border border-slate-200 p-7"
                >
                  <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
                    {s.title}
                  </h2>
                  <p className="mt-3 text-lg leading-relaxed text-mist">
                    {s.body}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1}>
            <p className="mt-10 text-center font-mono text-base uppercase tracking-[0.18em] text-faint">
              Questions? todd@zddesignagency.com — a human answers.
            </p>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
